import React, { useState, useEffect, useCallback } from 'react';
import { clickable } from '../../../utils/a11y';
import { useNavigate } from 'react-router-dom';
import './ContractorDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import {
  FaBriefcase, FaCalendarAlt, FaUsers, FaIdBadge, FaBell,
  FaUserCircle, FaSignOutAlt, FaBars, FaTimes, FaCheckCircle,
  FaFileInvoiceDollar, FaPlus, FaMapMarkerAlt,
  FaPaperPlane, FaListUl, FaClipboardList, FaFileAlt, FaBuilding,
  FaEdit, FaTrash, FaBan, FaUserSlash, FaPaperclip, FaTools, FaCloudDownloadAlt, FaCertificate, FaTag, FaKey } from 'react-icons/fa';
import ContractorEquipment from './ContractorEquipment';
import Toolbox from './Toolbox';
import AssetTagger from './AssetTagger';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getUserRole, isContractorAdmin } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import NotificationBell from '../../shared/NotificationBell';
import JobCard from '../../shared/JobCard';
import JobCalendar from '../../shared/JobCalendar';
import StatusBadge from '../../shared/JobStatusBadge';
import UserRoleBadge from '../../shared/UserRoleBadge';
import RequiredQualificationsPanel from '../../shared/QualificationPickers/RequiredQualificationsPanel';
import AssignStaffStep from '../../shared/QualificationPickers/AssignStaffStep';
import ManageQualificationsModal from '../../shared/QualificationPickers/ManageQualificationsModal';

const ContractorDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [loading, setLoading] = useState(true);

  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [companyStaff, setCompanyStaff] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [jobFilter, setJobFilter] = useState('all');
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [addStaffStep, setAddStaffStep] = useState('enter');
  const [addStaffForm, setAddStaffForm] = useState({ email: '', accountType: 'contractor_staff', tagIds: [], tagNames: '' });
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [companyTags, setCompanyTags] = useState([]);
  const [addStaffError, setAddStaffError] = useState('');
  const [addStaffSubmitting, setAddStaffSubmitting] = useState(false);
  const [staffTagFilter, setStaffTagFilter] = useState('');
  const [staffToEdit, setStaffToEdit] = useState(null);
  const [staffToRemove, setStaffToRemove] = useState(null);
  const [staffToSuspend, setStaffToSuspend] = useState(null);
  const [staffForQuals, setStaffForQuals] = useState(null);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [staffForProperties, setStaffForProperties] = useState(null);
  const [companyProperties, setCompanyProperties] = useState([]);
  const [staffPropertyIds, setStaffPropertyIds] = useState([]);
  const [staffPropertiesLoading, setStaffPropertiesLoading] = useState(false);
  const [staffPropertiesSaving, setStaffPropertiesSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    const role = getUserRole();
    if (role !== 'contractor') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    // Contractor Staff must use the staff dashboard, not this admin dashboard
    if (!isContractorAdmin()) {
      navigate('/contractor/staff-dashboard', { replace: true });
      return;
    }
    fetchProfile();
    fetchProperties();
    fetchJobs();
    fetchCalendar();
    fetchCompanyStaff();
  }, [navigate]);

  const fetchCompanyStaff = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/contractor/staff'), { headers });
      if (res.ok) setCompanyStaff(await res.json());
    } catch (e) { console.error('Failed to fetch company staff:', e); }
  }, [headers]);

  const fetchCompanyTags = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/contractor/tags'), { headers });
      if (res.ok) setCompanyTags(await res.json());
    } catch (e) { console.error('Failed to fetch tags:', e); }
  }, [headers]);

  const fetchProperties = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/properties'), { headers });
      if (res.ok) {
        const list = await res.json();
        setProperties(list);
      }
    } catch (e) { console.error('Failed to fetch properties:', e); }
  }, [headers]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), { headers });
      if (res.ok) {
        const data = await res.json();
        setUserName(`${data.firstName} ${data.lastName}`);
        setCompanyName(data.companyName || '');
        setCompanyCode(data.companyCode || '');
      }
    } catch (e) { /* silent */ }
  };

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/jobs'), { headers });
      if (res.ok) setJobs(await res.json());
    } catch (e) { console.error('Failed to fetch jobs:', e); }
    finally { setLoading(false); }
  }, []);

  const fetchCalendar = useCallback(async () => {
    try {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      const res = await fetch(getApiEndpoint(`/api/jobs/calendar?start=${start}&end=${end}`), { headers });
      if (res.ok) setCalendarEvents(await res.json());
    } catch (e) { /* silent */ }
  }, []);

  const fetchJobDetail = async (id) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${id}`), { headers });
      if (res.ok) {
        const data = await res.json();
        setSelectedJob(data);
      }
    } catch (e) { toast.error('Failed to load job'); }
  };

  const handleSubmitQuote = async (jobId, quoteData) => {
    try {
      const formData = new FormData();
      formData.append('message', quoteData.message || '');
      formData.append('notes', quoteData.notes || '');
      if (quoteData.price !== undefined && quoteData.price !== '') formData.append('price', String(quoteData.price));
      // Quote-acceptance staffing step (P1-16): send the picked engineer IDs.
      if (Array.isArray(quoteData.assigned_user_ids) && quoteData.assigned_user_ids.length > 0) {
        formData.append('assigned_user_ids', JSON.stringify(quoteData.assigned_user_ids));
      }
      (quoteData.files || []).forEach(f => formData.append('files', f));
      const res = await fetch(getApiEndpoint(`/api/jobs/${jobId}/quote`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      if (res.ok) {
        toast.success('Quote submitted successfully');
        setShowQuoteModal(false);
        fetchJobs();
        fetchJobDetail(jobId);
      } else {
        const err = await res.json().catch(() => ({}));
        if (res.status === 422 && err.missing_qualification_ids) {
          toast.error('Quote rejected — your assigned staff don\'t cover every required qualification.');
        } else {
          toast.error(err.message || 'Failed to submit quote');
        }
      }
    } catch (e) { toast.error('Failed to submit quote'); }
  };

  const handleAssignStaff = async (jobId, userId) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${jobId}/staff`), {
        method: 'POST', headers, body: JSON.stringify({ user_id: userId })
      });
      if (res.ok) {
        toast.success('Staff assigned to job');
        fetchJobDetail(jobId);
      } else {
        const err = await res.json();
        toast.error(err.message || 'Failed to assign staff');
      }
    } catch (e) { toast.error('Failed to assign staff'); }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('isCompanyAdmin');
    navigate('/login');
    toast.success('Logged out');
  };

  const handleLookupStaff = async (e) => {
    e.preventDefault();
    setAddStaffError('');
    if (!addStaffForm.email?.trim()) {
      setAddStaffError('Email or CloudSafe ID is required');
      return;
    }
    setAddStaffSubmitting(true);
    try {
      const q = encodeURIComponent(addStaffForm.email.trim());
      const res = await fetch(getApiEndpoint(`/api/contractor/staff/lookup?email=${q}`), { headers });
      const data = await res.json();
      if (res.ok && data.found) {
        setVerifiedUser(data);
        setAddStaffStep('verify');
      } else {
        setAddStaffError(data.message || 'No user found. They must register first.');
      }
    } catch (e) {
      setAddStaffError('Failed to lookup user');
    } finally {
      setAddStaffSubmitting(false);
    }
  };

  const handleConfirmAddStaff = async (e) => {
    e.preventDefault();
    if (!verifiedUser) return;
    setAddStaffError('');
    setAddStaffSubmitting(true);
    const tagNames = addStaffForm.tagNames
      ? addStaffForm.tagNames.split(/[,;]/).map(s => s.trim()).filter(Boolean)
      : [];
    try {
      const res = await fetch(getApiEndpoint('/api/contractor/staff/add'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          targetUserId: verifiedUser.id,
          accountType: addStaffForm.accountType,
          tagIds: addStaffForm.tagIds,
          tagNames
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setAddStaffError(data.message || 'Failed to add staff');
        return;
      }
      toast.success(data.message);
      setShowAddStaffModal(false);
      setAddStaffStep('enter');
      setVerifiedUser(null);
      setAddStaffForm({ email: '', accountType: 'contractor_staff', tagIds: [], tagNames: '' });
      fetchCompanyStaff();
      fetchCompanyTags();
    } catch (e) {
      setAddStaffError('Failed to add staff');
    } finally {
      setAddStaffSubmitting(false);
    }
  };

  const handleRemoveStaff = async () => {
    if (!staffToRemove) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/contractor/staff/${staffToRemove.id}`), {
        method: 'DELETE',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setStaffToRemove(null);
        fetchCompanyStaff();
      } else {
        toast.error(data.message || 'Failed to remove staff');
      }
    } catch (e) {
      toast.error('Failed to remove staff');
    }
  };

  const handleSuspendStaff = async () => {
    if (!staffToSuspend) return;
    const newStatus = staffToSuspend.contractor_staff_status === 'suspended' ? 'active' : 'suspended';
    try {
      const res = await fetch(getApiEndpoint(`/api/contractor/staff/${staffToSuspend.id}/status`), {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setStaffToSuspend(null);
        fetchCompanyStaff();
      } else {
        toast.error(data.message || 'Failed to update status');
      }
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleCreateJob = async (formData, filesToUpload = []) => {
    try {
      const { property_id, area, title, description, scheduled_date, scheduled_time, staff_ids, message, quote_price, quote_notes } = formData;
      const payload = {
        property_id,
        area,
        title,
        description,
        scheduled_date,
        scheduled_time: scheduled_time || null,
        staff_ids: staff_ids || [],
        message: message || null,
        quote_notes: quote_notes || null
      };
      if (quote_price != null && quote_price !== '' && !isNaN(parseFloat(quote_price))) {
        payload.quote_price = parseFloat(quote_price);
      }
      const res = await fetch(getApiEndpoint('/api/contractor/jobs'), {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.message || 'Failed to create job');
        return;
      }
      const jobId = data.id;
      if (filesToUpload.length > 0) {
        const formDataUpload = new FormData();
        filesToUpload.forEach(f => formDataUpload.append('files', f));
        const uploadRes = await fetch(getApiEndpoint(`/api/jobs/${jobId}/attachments`), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formDataUpload
        });
        if (!uploadRes.ok) {
          toast.warning('Job created but some files could not be uploaded');
        }
      }
      toast.success('Job created successfully. The Facility Manager has been notified.');
      setShowCreateJob(false);
      fetchJobs();
      fetchCalendar();
    } catch (e) {
      toast.error('Failed to create job');
    }
  };

  const handleUpdateStaffTags = async (tagIds, tagNames) => {
    if (!staffToEdit) return;
    try {
      const tagNamesArr = tagNames ? tagNames.split(/[,;]/).map(s => s.trim()).filter(Boolean) : [];
      const res = await fetch(getApiEndpoint(`/api/contractor/staff/${staffToEdit.id}`), {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ tagIds, tagNames: tagNamesArr })
      });
      if (res.ok) {
        toast.success('Staff updated');
        setStaffToEdit(null);
        fetchCompanyStaff();
      } else {
        const d = await res.json();
        toast.error(d.message || 'Failed to update');
      }
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const openStaffProperties = async (staff) => {
    setStaffForProperties(staff);
    setStaffPropertiesLoading(true);
    setStaffPropertyIds([]);
    setCompanyProperties([]);
    try {
      const [compRes, staffRes] = await Promise.all([
        fetch(getApiEndpoint('/api/contractor/properties'), { headers }),
        fetch(getApiEndpoint(`/api/contractor/staff/${staff.id}/properties`), { headers })
      ]);
      if (compRes.ok) setCompanyProperties(await compRes.json());
      if (staffRes.ok) {
        const assigned = await staffRes.json();
        setStaffPropertyIds(assigned.map(p => p.id));
      }
    } catch (e) {
      toast.error('Failed to load properties');
    } finally {
      setStaffPropertiesLoading(false);
    }
  };

  const toggleStaffProperty = (propertyId) => {
    setStaffPropertyIds(prev =>
      prev.includes(propertyId) ? prev.filter(id => id !== propertyId) : [...prev, propertyId]
    );
  };

  const saveStaffProperties = async () => {
    if (!staffForProperties) return;
    setStaffPropertiesSaving(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/contractor/staff/${staffForProperties.id}/properties`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ property_ids: staffPropertyIds })
      });
      if (res.ok) {
        toast.success('Property access saved');
        setStaffForProperties(null);
      } else {
        const d = await res.json();
        toast.error(d.message || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save property access');
    } finally {
      setStaffPropertiesSaving(false);
    }
  };

  const invitedJobs = jobs.filter(j => j.status === 'invited');
  const quotedJobs = jobs.filter(j => j.status === 'quoted');
  const approvedJobs = jobs.filter(j => ['approved', 'active'].includes(j.status));
  const completedJobs = jobs.filter(j => j.status === 'completed');

  const sidebarSections = [
    {
      items: [
        { id: 'overview', label: 'Overview', icon: FaListUl },
        { id: 'properties', label: 'Properties', icon: FaBuilding, link: '/contractor/properties' },
        { id: 'jobs', label: 'Jobs', icon: FaBriefcase, badge: invitedJobs.length || null },
        { id: 'calendar', label: 'Calendar', icon: FaCalendarAlt },
        { id: 'equipment', label: 'Equipment', icon: FaTools },
        { id: 'staff', label: 'Staff', icon: FaUsers },
        { id: 'tasks', label: 'Tasks', icon: FaClipboardList, link: '/contractor/tasks' },
        { id: 'inspections', label: 'Inspections', icon: FaClipboardList, link: '/contractor/inspections' },
        { id: 'reports', label: 'Reports', icon: FaFileAlt, link: '/contractor/reports' },
        { id: 'id-badge', label: 'ID Badge', icon: FaIdBadge, link: '/contractor/id-badge' },
      ]
    },
    {
      items: [
        { id: 'toolbox', label: 'Toolbox', icon: FaTools },
      ]
    },
  ];
  const sidebarItems = sidebarSections.flatMap(s => s.items);

  return (
    <div className="contractor-dashboard">
      <header className="contractor-header">
        <div className="header-left">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="header-logo" />
          <h1 className="header-title">Contractor Dashboard</h1>
        </div>
        <div className="header-right">
          <UserRoleBadge />
          {(companyName || companyCode) && (
            <span className="header-company-badge" title="Your company and CloudSafe ID">
              {companyName && <span className="header-company-name">{companyName}</span>}
              {companyCode && (
                <span className="header-company-code">{companyName ? ` • ${companyCode}` : companyCode}</span>
              )}
            </span>
          )}
          <NotificationBell />
          <div className="header-user" {...clickable(() => navigate('/contractor/profile'), { label: 'Open your profile' })}>
            <FaUserCircle className="header-user-icon" />
            <span className="header-user-name">{userName}</span>
          </div>
          <button className="header-logout" onClick={handleLogout}><FaSignOutAlt /></button>
        </div>
      </header>

      <div className="contractor-layout">
        <aside className={`contractor-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <nav className="sidebar-nav">
            {sidebarSections.map((section, si) => (
              <div key={si} className="sidebar-section">
                {section.label && (
                  <div className="sidebar-section-label">{section.label}</div>
                )}
                {section.items.map(item => (
                  item.link ? (
                    <Link
                      key={item.id}
                      to={item.link}
                      className={`sidebar-item ${activeSection === item.id ? 'active' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="sidebar-icon" />
                      <span>{item.label}</span>
                      {item.badge && <span className="sidebar-badge">{item.badge}</span>}
                    </Link>
                  ) : (
                    <button
                      key={item.id}
                      className={`sidebar-item ${activeSection === item.id ? 'active' : ''}`}
                      onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                    >
                      <item.icon className="sidebar-icon" />
                      <span>{item.label}</span>
                      {item.badge && <span className="sidebar-badge">{item.badge}</span>}
                    </button>
                  )
                ))}
              </div>
            ))}
          </nav>
        </aside>
        {sidebarOpen && <div className="contractor-sidebar-overlay show" onClick={() => setSidebarOpen(false)} />}

        <main className="contractor-main">
          {activeSection === 'overview' && renderOverview()}
          {activeSection === 'jobs' && renderJobs()}
          {activeSection === 'calendar' && renderCalendarView()}
          {activeSection === 'equipment' && <ContractorEquipment />}
          {activeSection === 'staff' && renderStaff(companyStaff)}
          {activeSection === 'toolbox' && <Toolbox onNavigate={setActiveSection} />}
          {activeSection === 'asset-tagger' && <AssetTagger />}
        </main>
      </div>

      {/* Mobile bottom nav — 5 items: Overview, Jobs, Calendar, Staff, More(sidebar) */}
      <div className="cs-bottom-nav">
        <div className="cs-bottom-nav-items">
          {[
            { id: 'overview', label: 'Overview', icon: FaListUl },
            { id: 'jobs', label: 'Jobs', icon: FaBriefcase, badge: invitedJobs.length || null },
            { id: 'calendar', label: 'Calendar', icon: FaCalendarAlt },
            { id: 'staff', label: 'Staff', icon: FaUsers },
          ].map(item => (
            <button key={item.id} className={`cs-bottom-nav-item ${activeSection === item.id ? 'active' : ''}`} onClick={() => setActiveSection(item.id)}>
              <item.icon />
              <span>{item.label}</span>
              {item.badge && <span className="cs-bottom-nav-badge">{item.badge}</span>}
            </button>
          ))}
          <button className={`cs-bottom-nav-item ${sidebarOpen ? 'active' : ''}`} onClick={() => setSidebarOpen(prev => !prev)}>
            <FaBars />
            <span>More</span>
          </button>
        </div>
      </div>

      {showCreateJob && (
        <ContractorCreateJobModal
          properties={properties}
          companyStaff={companyStaff}
          onSubmit={handleCreateJob}
          onClose={() => setShowCreateJob(false)}
        />
      )}

      {selectedJob && (
        <JobDetailPanel
          job={selectedJob}
          companyStaff={companyStaff}
          onClose={() => setSelectedJob(null)}
          onSubmitQuote={(data) => handleSubmitQuote(selectedJob.id, data)}
          onAssignStaff={(userId) => handleAssignStaff(selectedJob.id, userId)}
          onNoteAdded={() => fetchJobDetail(selectedJob.id)}
          showQuoteModal={showQuoteModal}
          setShowQuoteModal={setShowQuoteModal}
          headers={headers}
          navigate={navigate}
        />
      )}
    </div>
  );

  function renderOverview() {
    if (loading) {
      return (
        <div className="con-overview">
          <div className="con-skeleton" style={{ width: '220px', height: '28px', marginBottom: '8px' }} />
          <div className="con-skeleton" style={{ width: '180px', height: '16px', marginBottom: '24px' }} />
          <div className="con-stats-grid">
            {[1,2,3,4].map(i => <div key={i} className="con-skeleton con-skeleton-stat" />)}
          </div>
          {[1,2,3].map(i => <div key={i} className="con-skeleton con-skeleton-card" />)}
        </div>
      );
    }

    return (
      <div className="con-overview">
        <h2 className="section-title">Welcome back, {userName.split(' ')[0]}</h2>
        <p className="con-section-subtitle">
          {invitedJobs.length > 0
            ? `You have ${invitedJobs.length} new job invite${invitedJobs.length !== 1 ? 's' : ''} to review`
            : 'Here\u2019s your work overview'}
        </p>

        <div className="con-stats-grid">
          <div className="con-stat-card" {...clickable(() => setActiveSection('jobs'), { label: 'New invites — open jobs' })}>
            <div className="con-stat-icon invite"><FaBell /></div>
            <div className="con-stat-content">
              <span className="con-stat-value">{invitedJobs.length}</span>
              <span className="con-stat-label">New Invites</span>
            </div>
          </div>
          <div className="con-stat-card" {...clickable(() => setActiveSection('jobs'), { label: 'Quoted — open jobs' })}>
            <div className="con-stat-icon quoted"><FaFileInvoiceDollar /></div>
            <div className="con-stat-content">
              <span className="con-stat-value">{quotedJobs.length}</span>
              <span className="con-stat-label">Quoted</span>
            </div>
          </div>
          <div className="con-stat-card" {...clickable(() => setActiveSection('jobs'), { label: 'Active jobs — open jobs' })}>
            <div className="con-stat-icon active"><FaBriefcase /></div>
            <div className="con-stat-content">
              <span className="con-stat-value">{approvedJobs.length}</span>
              <span className="con-stat-label">Active Jobs</span>
            </div>
          </div>
          <div className="con-stat-card">
            <div className="con-stat-icon done"><FaCheckCircle /></div>
            <div className="con-stat-content">
              <span className="con-stat-value">{completedJobs.length}</span>
              <span className="con-stat-label">Completed</span>
            </div>
          </div>
        </div>

        {invitedJobs.length > 0 && (
          <div className="con-section-card">
            <div className="con-section-header">
              <h3><FaBell /> New Job Invites</h3>
              <button className="view-all-btn" onClick={() => setActiveSection('jobs')}>View All</button>
            </div>
            <div className="con-section-body">
              {invitedJobs.slice(0, 3).map(j => (
                <JobCard key={j.id} job={j} compact onClick={() => fetchJobDetail(j.id)} />
              ))}
            </div>
          </div>
        )}

        {approvedJobs.length > 0 && (
          <div className="con-section-card">
            <div className="con-section-header">
              <h3><FaBriefcase /> Active & Approved Jobs</h3>
              <button className="view-all-btn" onClick={() => { setJobFilter('approved'); setActiveSection('jobs'); }}>View All</button>
            </div>
            <div className="con-section-body">
              {approvedJobs.slice(0, 5).map(j => (
                <JobCard key={j.id} job={j} compact onClick={() => fetchJobDetail(j.id)} />
              ))}
            </div>
          </div>
        )}

        {jobs.length === 0 && (
          <div className="con-empty-state">
            <FaBriefcase className="empty-icon" />
            <h3>No Jobs Yet</h3>
            <p>When a Facility Manager sends you a job invite, it will appear here. You can also create jobs for properties you&apos;re assigned to.</p>
            <button className="cs-btn cs-btn-primary" onClick={() => setShowCreateJob(true)}>
              <FaPlus /> Create a Job
            </button>
          </div>
        )}

        {(() => {
          const seen = new Set();
          const uniqueProps = jobs.filter(j => {
            if (!j.property_name || seen.has(j.property_id)) return false;
            seen.add(j.property_id);
            return true;
          });
          if (uniqueProps.length === 0) return null;
          return (
            <div className="con-section-card">
              <div className="con-section-header">
                <h3><FaMapMarkerAlt /> Properties</h3>
              </div>
              <div className="con-properties-grid">
                {uniqueProps.map(j => (
                  <div key={j.property_id} className="con-property-chip">
                    <span className="con-property-name">{j.property_name}</span>
                    <span className="con-property-jobs">
                      {jobs.filter(x => x.property_id === j.property_id).length} job{jobs.filter(x => x.property_id === j.property_id).length !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  function renderJobs() {
    const byProperty = selectedPropertyId ? jobs.filter(j => j.property_id === selectedPropertyId) : jobs;
    const filtered = jobFilter === 'all' ? byProperty : byProperty.filter(j => j.status === jobFilter);

    return (
      <div className="con-jobs">
        <div className="con-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', flex: 1 }}>
            <h2 className="section-title" style={{ margin: 0 }}>Jobs</h2>
            <select
              className="cs-select"
              style={{ width: 'auto', minWidth: '160px', flex: '1 1 160px', maxWidth: '240px' }}
              value={selectedPropertyId ?? ''}
              onChange={e => setSelectedPropertyId(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">All properties</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <select
              className="cs-select"
              style={{ width: 'auto', flex: '1 1 140px', maxWidth: '200px' }}
              value={jobFilter}
              onChange={e => setJobFilter(e.target.value)}
            >
              <option value="all">All ({byProperty.length})</option>
              <option value="invited">Invites ({byProperty.filter(j=>j.status==='invited').length})</option>
              <option value="quoted">Quoted ({byProperty.filter(j=>j.status==='quoted').length})</option>
              <option value="approved">Approved ({byProperty.filter(j=>j.status==='approved').length})</option>
              <option value="active">Active ({byProperty.filter(j=>j.status==='active').length})</option>
              <option value="completed">Completed ({byProperty.filter(j=>j.status==='completed').length})</option>
            </select>
          </div>
          <button className="cs-btn cs-btn-primary" onClick={() => setShowCreateJob(true)}>
            <FaPlus /> Create Job
          </button>
        </div>

        {loading ? (
          <div>
            {[1,2,3].map(i => <div key={i} className="con-skeleton con-skeleton-card" />)}
          </div>
        ) : filtered.length > 0 ? (
          <div className="con-jobs-list">
            {filtered.map(j => (
              <JobCard key={j.id} job={j} onClick={() => fetchJobDetail(j.id)} />
            ))}
          </div>
        ) : (
          <div className="con-empty-state">
            <FaBriefcase className="empty-icon" />
            <h3>No Jobs Found</h3>
            <p>No jobs match this filter. Create a job for a property you&apos;re assigned to, or wait for the Facility Manager to send you an invite.</p>
          </div>
        )}
      </div>
    );
  }

  function renderCalendarView() {
    return (
      <div className="con-calendar">
        <h2 className="section-title">Calendar</h2>
        <div className="calendar-legend" style={{marginBottom:'1rem',display:'flex',gap:'0.75rem',flexWrap:'wrap'}}>
          <span className="legend-item"><span className="legend-dot" style={{background:'#e0e7ff'}} /> Invited</span>
          <span className="legend-item"><span className="legend-dot" style={{background:'#fef3c7'}} /> Quoted</span>
          <span className="legend-item"><span className="legend-dot" style={{background:'#d1fae5'}} /> Approved</span>
          <span className="legend-item"><span className="legend-dot" style={{background:'#dbeafe'}} /> Active</span>
        </div>
        <JobCalendar
          events={calendarEvents}
          onEventClick={(ev) => fetchJobDetail(ev.id)}
          onDateChange={(start, end) => {
            fetch(getApiEndpoint(`/api/jobs/calendar?start=${start}&end=${end}`), { headers })
              .then(r => r.ok ? r.json() : []).then(setCalendarEvents);
          }}
        />
      </div>
    );
  }

  function renderStaff(staffList = []) {
    const filteredStaff = staffTagFilter
      ? staffList.filter(u => (u.tags || []).some(t => t.name.toLowerCase().includes(staffTagFilter.toLowerCase())))
      : staffList;
    const allTagNames = [...new Set(staffList.flatMap(u => (u.tags || []).map(t => t.name)))].sort();

    return (
      <div className="con-staff">
        <div className="con-toolbar">
          <div style={{ flex: 1 }}>
            <h2 className="section-title" style={{ margin: 0 }}>My Staff</h2>
            <p className="con-section-subtitle" style={{ marginBottom: 0, marginTop: '0.25rem' }}>
              Staff members from your company who can be assigned to jobs.
            </p>
          </div>
          <button className="cs-btn cs-btn-primary" onClick={() => {
            setShowAddStaffModal(true);
            setAddStaffError('');
            setAddStaffStep('enter');
            setVerifiedUser(null);
            setAddStaffForm({ email: '', accountType: 'contractor_staff', tagIds: [], tagNames: '' });
            fetchCompanyTags();
          }}>
            <FaPlus /> Add Staff
          </button>
        </div>

        {staffList.length > 0 && allTagNames.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <select
              className="cs-select"
              style={{ maxWidth: '200px' }}
              value={staffTagFilter}
              onChange={e => setStaffTagFilter(e.target.value)}
            >
              <option value="">All tags</option>
              {allTagNames.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        )}

        {filteredStaff.length > 0 ? (
            <>
              {/* Desktop table */}
              <div className="con-staff-table-wrap">
                <table className="con-staff-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>CloudSafe ID</th>
                      <th>Role / Tags</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaff.map(u => (
                      <tr key={u.id} className="con-staff-row-hover">
                        <td>
                          <span style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</span>
                          {u.contractor_staff_status === 'suspended' && <span className="con-staff-suspended" style={{ marginLeft: '0.5rem' }}>Suspended</span>}
                        </td>
                        <td style={{ fontSize: '0.88rem', color: '#64748b' }}>{u.email}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{u.cloudSafeId || `CS-CONTRACTOR-${u.id}`}</td>
                        <td>
                          {!!u.is_company_admin && <span style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>Company Admin</span>}
                          {(u.tags || []).length > 0 && (
                            <span style={{ marginLeft: u.is_company_admin ? '0.5rem' : 0 }}>
                              {(u.tags || []).map(t => (
                                <span key={t.id} className="con-staff-tag">{t.name}</span>
                              ))}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button type="button" className="con-staff-action-btn" onClick={() => setStaffForQuals(u)} title="Manage qualifications"><FaCertificate /></button>
                          <button type="button" className="con-staff-action-btn" onClick={() => openStaffProperties(u)} title="Property access"><FaKey /></button>
                          <button type="button" className="con-staff-action-btn" onClick={() => setStaffToEdit(u)} title="Edit tags"><FaEdit /></button>
                          {!u.is_company_admin && (
                            <>
                              <button type="button" className="con-staff-action-btn" onClick={() => setStaffToSuspend(u)} title={u.contractor_staff_status === 'suspended' ? 'Reactivate' : 'Suspend'}>
                                {u.contractor_staff_status === 'suspended' ? <FaUserSlash /> : <FaBan />}
                              </button>
                              <button type="button" className="con-staff-action-btn con-staff-action-remove" onClick={() => setStaffToRemove(u)} title="Remove from company"><FaTrash /></button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="con-staff-cards">
                {filteredStaff.map(u => (
                  <div key={u.id} className="con-staff-card">
                    <div className="con-staff-card-header">
                      <div>
                        <div className="con-staff-card-name">
                          {u.first_name} {u.last_name}
                          {u.contractor_staff_status === 'suspended' && <span className="con-staff-suspended" style={{ marginLeft: '0.5rem' }}>Suspended</span>}
                        </div>
                        <div className="con-staff-card-email">{u.email}</div>
                      </div>
                      {!!u.is_company_admin && <span style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600, whiteSpace: 'nowrap' }}>Admin</span>}
                    </div>
                    <div className="con-staff-card-meta">
                      <span className="con-staff-card-id">{u.cloudSafeId || `CS-CONTRACTOR-${u.id}`}</span>
                      {(u.tags || []).map(t => (
                        <span key={t.id} className="con-staff-tag">{t.name}</span>
                      ))}
                    </div>
                    <div className="con-staff-card-actions">
                      <button type="button" className="con-staff-action-btn" onClick={() => setStaffToEdit(u)} title="Edit tags"><FaEdit /></button>
                      {!u.is_company_admin && (
                        <>
                          <button type="button" className="con-staff-action-btn" onClick={() => setStaffToSuspend(u)} title={u.contractor_staff_status === 'suspended' ? 'Reactivate' : 'Suspend'}>
                            {u.contractor_staff_status === 'suspended' ? <FaUserSlash /> : <FaBan />}
                          </button>
                          <button type="button" className="con-staff-action-btn con-staff-action-remove" onClick={() => setStaffToRemove(u)} title="Remove from company"><FaTrash /></button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
        ) : (
          <div className="con-empty-state">
            <FaUsers className="empty-icon" />
            <h3>Staff Management</h3>
            <p>Staff members registered under your company will appear here. Use Manage Staff to add existing CloudSafe users. You can assign them to jobs from the job detail view.</p>
          </div>
        )}

        {showAddStaffModal && (
          <AddStaffModal
            step={addStaffStep}
            verifiedUser={verifiedUser}
            form={addStaffForm}
            setForm={setAddStaffForm}
            companyTags={companyTags}
            fetchCompanyTags={fetchCompanyTags}
            error={addStaffError}
            submitting={addStaffSubmitting}
            onLookup={handleLookupStaff}
            onConfirm={handleConfirmAddStaff}
            onBack={() => { setAddStaffStep('enter'); setVerifiedUser(null); setAddStaffError(''); }}
            onClose={() => !addStaffSubmitting && (setShowAddStaffModal(false), setAddStaffStep('enter'), setVerifiedUser(null))}
          />
        )}

        {staffForQuals && (
          <ManageQualificationsModal
            user={staffForQuals}
            onClose={() => setStaffForQuals(null)}
          />
        )}

        {staffToEdit && (
          <EditStaffModal
            staff={staffToEdit}
            companyTags={companyTags}
            fetchCompanyTags={fetchCompanyTags}
            onSave={handleUpdateStaffTags}
            onClose={() => setStaffToEdit(null)}
          />
        )}

        {staffForProperties && (
          <div className="cs-modal-overlay" onClick={() => !staffPropertiesSaving && setStaffForProperties(null)}>
            <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
              <div className="cs-modal-header">
                <h3>Property Access — {staffForProperties.first_name} {staffForProperties.last_name}</h3>
                <button type="button" className="cs-modal-close" onClick={() => !staffPropertiesSaving && setStaffForProperties(null)}><FaTimes /></button>
              </div>
              <div className="cs-modal-body">
                {staffPropertiesLoading ? (
                  <p style={{ color: '#64748b', textAlign: 'center', padding: '1rem 0' }}>Loading properties...</p>
                ) : companyProperties.length === 0 ? (
                  <p style={{ color: '#64748b' }}>No properties assigned to your company yet. Ask your Facility Manager to add your company to a property.</p>
                ) : (
                  <>
                    <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
                      Select which properties this staff member can access. If none are selected, they can see all company properties.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '320px', overflowY: 'auto' }}>
                      {companyProperties.map(p => {
                        const selected = staffPropertyIds.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => toggleStaffProperty(p.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.75rem',
                              padding: '0.625rem 0.875rem', borderRadius: '8px', cursor: 'pointer',
                              border: selected ? '2px solid #2563eb' : '2px solid #e2e8f0',
                              background: selected ? '#eff6ff' : '#fff',
                              textAlign: 'left', width: '100%', transition: 'all 0.15s'
                            }}
                          >
                            <span style={{
                              width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                              background: selected ? '#2563eb' : '#e2e8f0',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff'
                            }}>
                              {selected && '✓'}
                            </span>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>{p.name}</div>
                              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{p.address}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                      <button type="button" className="cs-btn cs-btn-outline" onClick={() => setStaffForProperties(null)} disabled={staffPropertiesSaving}>Cancel</button>
                      <button type="button" className="cs-btn cs-btn-primary" onClick={saveStaffProperties} disabled={staffPropertiesSaving}>
                        {staffPropertiesSaving ? 'Saving...' : 'Save Access'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {staffToRemove && (
          <div className="cs-modal-overlay" onClick={() => setStaffToRemove(null)}>
            <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
              <div className="cs-modal-header">
                <h3>Remove Staff</h3>
                <button type="button" className="cs-modal-close" onClick={() => setStaffToRemove(null)}><FaTimes /></button>
              </div>
              <div className="cs-modal-body">
                <p>Remove <strong>{staffToRemove.first_name} {staffToRemove.last_name}</strong> from your company? They will lose access to your properties and jobs.</p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button type="button" className="cs-btn cs-btn-outline" onClick={() => setStaffToRemove(null)}>Cancel</button>
                  <button type="button" className="cs-btn cs-btn-primary" style={{ background: '#dc2626' }} onClick={handleRemoveStaff}>Remove</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {staffToSuspend && (
          <div className="cs-modal-overlay" onClick={() => setStaffToSuspend(null)}>
            <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
              <div className="cs-modal-header">
                <h3>{staffToSuspend.contractor_staff_status === 'suspended' ? 'Reactivate Staff' : 'Suspend Staff'}</h3>
                <button type="button" className="cs-modal-close" onClick={() => setStaffToSuspend(null)}><FaTimes /></button>
              </div>
              <div className="cs-modal-body">
                <p>
                  {staffToSuspend.contractor_staff_status === 'suspended'
                    ? `Reactivate ${staffToSuspend.first_name} ${staffToSuspend.last_name}? They will be able to access jobs again.`
                    : `Suspend ${staffToSuspend.first_name} ${staffToSuspend.last_name}? They will not be able to access jobs until reactivated.`}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button type="button" className="cs-btn cs-btn-outline" onClick={() => setStaffToSuspend(null)}>Cancel</button>
                  <button type="button" className="cs-btn cs-btn-primary" onClick={handleSuspendStaff}>
                    {staffToSuspend.contractor_staff_status === 'suspended' ? 'Reactivate' : 'Suspend'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

};

/* ─── Contractor Create Job Modal ─── */
function ContractorCreateJobModal({ properties, companyStaff, onSubmit, onClose }) {
  const [form, setForm] = useState({
    property_id: '', area: '', title: '', description: '', scheduled_date: '', scheduled_time: '',
    staff_ids: [], message: '', quote_price: '', quote_notes: ''
  });
  const [quoteFiles, setQuoteFiles] = useState([]);
  const [messageFiles, setMessageFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.property_id || !form.area || !form.title || !form.scheduled_date) {
      toast.error('Please fill in Property, Area, Title, and Scheduled Date');
      return;
    }
    setSubmitting(true);
    try {
      const allFiles = [...quoteFiles, ...messageFiles];
      await onSubmit(
        { ...form, staff_ids: form.staff_ids },
        allFiles
      );
    } catch (err) {
      toast.error(err.message || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStaff = (id) => {
    setForm(prev => ({
      ...prev,
      staff_ids: prev.staff_ids.includes(id)
        ? prev.staff_ids.filter(s => s !== id)
        : [...prev.staff_ids, id]
    }));
  };

  const staffWithoutAdmin = (companyStaff || []).filter(u => !u.is_company_admin && u.contractor_staff_status !== 'suspended');

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="cs-modal-header">
          <h3>Create Job</h3>
          <button type="button" className="cs-modal-close" onClick={onClose} disabled={submitting}><FaTimes /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="cs-modal-body">
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>
              Create a job for a property you&apos;re assigned to. The Facility Manager will be notified and can approve your quote.
            </p>
            <div className="cs-form-group">
              <label>Property *</label>
              <select className="cs-select" value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })} required>
                <option value="">Select property...</option>
                {(properties || []).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="cs-form-group">
              <label>Area *</label>
              <select className="cs-select" value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} required>
                <option value="">Select area...</option>
                <option value="Roof Safety">Roof Safety</option>
                <option value="Fire Safety">Fire Safety</option>
                <option value="Electrical Safety">Electrical Safety</option>
                <option value="General Maintenance">General Maintenance</option>
                <option value="Building Maintenance">Building Maintenance</option>
                <option value="Equipment Safety">Equipment Safety</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="cs-form-group">
              <label>Job Title *</label>
              <input className="cs-input" placeholder="e.g. Annual Anchor Point Testing" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div className="cs-form-group">
              <label>Description</label>
              <textarea className="cs-textarea" placeholder="Describe the work required..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div className="cs-form-group" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 140px' }}>
                <label>Date *</label>
                <input type="date" className="cs-input" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} required />
              </div>
              <div style={{ flex: '1 1 100px' }}>
                <label>Time (optional)</label>
                <input type="time" className="cs-input" value={form.scheduled_time} onChange={e => setForm({ ...form, scheduled_time: e.target.value })} />
              </div>
            </div>
            {staffWithoutAdmin.length > 0 && (
              <div className="cs-form-group">
                <label>Assign Staff</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {staffWithoutAdmin.map(u => (
                    <button
                      key={u.id}
                      type="button"
                      className={`cs-btn cs-btn-outline cs-btn-sm ${form.staff_ids.includes(u.id) ? 'active' : ''}`}
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => toggleStaff(u.id)}
                    >
                      {u.first_name} {u.last_name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="cs-form-group">
              <label>Quote (optional)</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <input type="number" step="0.01" min="0" className="cs-input" placeholder="Price" value={form.quote_price} onChange={e => setForm({ ...form, quote_price: e.target.value })} style={{ width: '120px' }} />
                <input type="text" className="cs-input" placeholder="Quote notes" value={form.quote_notes} onChange={e => setForm({ ...form, quote_notes: e.target.value })} style={{ flex: 1, minWidth: '140px' }} />
              </div>
              <small style={{ color: '#6b7280', display: 'block', marginTop: '0.25rem' }}>Upload quote PDF/images below</small>
            </div>
            <div className="cs-form-group">
              <label>Message to FM</label>
              <textarea className="cs-textarea" placeholder="Add a message for the Facility Manager..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} />
            </div>
            <div className="cs-form-group">
              <label>Upload Documents (quote PDF, images)</label>
              <input
                type="file"
                className="cs-input"
                accept=".pdf,.jpg,.jpeg,.png,.gif"
                multiple
                onChange={e => setQuoteFiles(Array.from(e.target.files || []))}
              />
              <small style={{ color: '#6b7280', display: 'block', marginTop: '0.25rem' }}>PDF, JPG, PNG. Max 10MB per file.</small>
            </div>
            <div className="cs-form-group">
              <label>Additional Images (optional)</label>
              <input
                type="file"
                className="cs-input"
                accept=".jpg,.jpeg,.png,.gif"
                multiple
                onChange={e => setMessageFiles(Array.from(e.target.files || []))}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="cs-btn cs-btn-outline" onClick={onClose} disabled={submitting}>Cancel</button>
              <button type="submit" className="cs-btn cs-btn-primary" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create Job'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Add Staff Modal (two-step: lookup then verify) ─── */
function AddStaffModal({ step, verifiedUser, form, setForm, companyTags, fetchCompanyTags, error, submitting, onLookup, onConfirm, onBack, onClose }) {
  const toggleTag = (id) => {
    const ids = form.tagIds || [];
    setForm({ ...form, tagIds: ids.includes(id) ? ids.filter(t => t !== id) : [...ids, id] });
  };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="cs-modal-header">
          <h3>{step === 'verify' ? 'Confirm & Add Staff' : 'Add Staff'}</h3>
          <button type="button" className="cs-modal-close" onClick={onClose} disabled={submitting}><FaTimes /></button>
        </div>
        {step === 'enter' ? (
          <form onSubmit={onLookup}>
            <div className="cs-modal-body">
              <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Add an existing CloudSafe user to your company. Enter their email or CloudSafe ID to look them up.
              </p>
              <div className="cs-form-group">
                <label>Email or CloudSafe ID</label>
                <input
                  className="cs-input"
                  type="text"
                  placeholder="e.g. engineer@example.com or CS-CONTRACTOR-123"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  disabled={submitting}
                />
              </div>
              {error && <p style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: '0.75rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="cs-btn cs-btn-outline" onClick={onClose} disabled={submitting}>Cancel</button>
                <button type="submit" className="cs-btn cs-btn-primary" disabled={submitting}>
                  {submitting ? 'Looking up…' : 'Find User'}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <form onSubmit={onConfirm}>
            <div className="cs-modal-body">
              <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Confirm this is the correct person, then choose their role and tags.
              </p>
              {verifiedUser && (
                <div className="con-staff-verify-box" style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #e2e8f0' }}>
                  <p style={{ margin: 0, fontWeight: 600 }}>{verifiedUser.firstName} {verifiedUser.lastName}</p>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.88rem', color: '#64748b' }}>{verifiedUser.email}</p>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', fontFamily: 'monospace', color: '#64748b' }}>{verifiedUser.cloudSafeId}</p>
                </div>
              )}
              <div className="cs-form-group">
                <label>Role</label>
                <select
                  className="cs-select"
                  value={form.accountType}
                  onChange={e => setForm({ ...form, accountType: e.target.value })}
                >
                  <option value="contractor_staff">Engineer</option>
                  <option value="contractor_admin">Contractor</option>
                </select>
              </div>
              <div className="cs-form-group">
                <label>Tags (optional)</label>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>Attach existing tags or create new ones:</p>
                {companyTags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
                    {companyTags.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        className={`cs-btn cs-btn-outline cs-btn-sm ${(form.tagIds || []).includes(t.id) ? 'active' : ''}`}
                        style={{ fontSize: '0.8rem' }}
                        onClick={() => toggleTag(t.id)}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                )}
                <input
                  className="cs-input"
                  type="text"
                  placeholder="New tags (comma-separated, e.g. Electrician, Roofing)"
                  value={form.tagNames}
                  onChange={e => setForm({ ...form, tagNames: e.target.value })}
                  style={{ marginTop: '0.25rem' }}
                />
              </div>
              {error && <p style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: '0.75rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="cs-btn cs-btn-outline" onClick={onBack} disabled={submitting}>Back</button>
                <button type="submit" className="cs-btn cs-btn-primary" disabled={submitting}>
                  {submitting ? 'Adding…' : 'Confirm & Add to Company'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ─── Edit Staff Modal (tags) ─── */
function EditStaffModal({ staff, companyTags, fetchCompanyTags, onSave, onClose }) {
  const [tagIds, setTagIds] = useState(staff?.tags?.map(t => t.id) || []);
  const [tagNames, setTagNames] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    fetchCompanyTags();
  }, [fetchCompanyTags]);
  const toggleTag = (id) => {
    setTagIds(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(tagIds, tagNames);
    setSaving(false);
    onClose();
  };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <div className="cs-modal-header">
          <h3>Edit Staff Tags</h3>
          <button type="button" className="cs-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        <form onSubmit={handleSave}>
          <div className="cs-modal-body">
            <p style={{ marginBottom: '1rem' }}><strong>{staff?.first_name} {staff?.last_name}</strong></p>
            <div className="cs-form-group">
              <label>Tags</label>
              {companyTags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
                  {companyTags.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      className={`cs-btn cs-btn-outline cs-btn-sm ${tagIds.includes(t.id) ? 'active' : ''}`}
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => toggleTag(t.id)}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              )}
              <input
                className="cs-input"
                type="text"
                placeholder="New tags (comma-separated)"
                value={tagNames}
                onChange={e => setTagNames(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="cs-btn cs-btn-outline" onClick={onClose}>Cancel</button>
              <button type="submit" className="cs-btn cs-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Job Detail Side Panel ──────────────────── */
const JobDetailPanel = ({ job, companyStaff = [], onClose, onSubmitQuote, onAssignStaff, onNoteAdded, showQuoteModal, setShowQuoteModal, headers, navigate }) => {
  const [quoteForm, setQuoteForm] = useState({ message: '', notes: '', files: [], assigned_user_ids: [] });
  // Quote-gate state (P1-13, P1-16)
  const [gateSatisfied, setGateSatisfied] = useState(true);
  const [gateRequirements, setGateRequirements] = useState([]);
  const [assignmentValid, setAssignmentValid] = useState(true);
  const [staffSearch, setStaffSearch] = useState('');
  const [staffTagFilter, setStaffTagFilter] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [noteText, setNoteText] = useState('');

  const staffSearchLower = (staffSearch || '').toLowerCase();
  const allTagNames = [...new Set((companyStaff || []).flatMap(u => (u.tags || []).map(t => t.name)))].sort();
  let staffResults = (companyStaff || []).filter(u => {
    if (!staffSearchLower) return true;
    const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
    const email = (u.email || '').toLowerCase();
    return name.includes(staffSearchLower) || email.includes(staffSearchLower);
  });
  if (staffTagFilter) {
    staffResults = staffResults.filter(u => (u.tags || []).some(t => t.name === staffTagFilter));
  }

  const handleQuote = (e) => {
    e.preventDefault();
    if (!quoteForm.files || quoteForm.files.length === 0) { toast.error('Please upload a quote document (PDF or image)'); return; }
    if (gateRequirements.length > 0 && !gateSatisfied) {
      toast.error('Your team doesn\'t meet the required qualifications for this job.');
      return;
    }
    if (gateRequirements.length > 0 && !assignmentValid) {
      toast.error('Assign enough qualified staff to cover every requirement before submitting.');
      return;
    }
    onSubmitQuote({
      message: quoteForm.message,
      notes: quoteForm.notes,
      files: quoteForm.files,
      assigned_user_ids: quoteForm.assigned_user_ids,
    });
    setQuoteForm({ message: '', notes: '', files: [], assigned_user_ids: [] });
  };

  // staffResults derived from companyStaff + staffSearch above; no API call needed

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${job.id}/notes`), {
        method: 'POST',
        headers: { ...headers },
        body: JSON.stringify({ note_text: noteText, note_type: 'general' })
      });
      if (res.ok) {
        toast.success('Note added');
        setNoteText('');
        onNoteAdded?.();
      } else {
        toast.error('Failed to add note');
      }
    } catch (e) { toast.error('Failed to add note'); }
  };

  const hasQuoted = job.quotes && job.quotes.some(q => q.status !== 'declined');
  const canQuote = job.status === 'invited' && !hasQuoted;
  const canAssignStaff = ['approved', 'active'].includes(job.status);

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="cs-modal-header">
          <h3>{job.title}</h3>
          <button className="cs-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="cs-modal-body">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
            <StatusBadge status={job.status} />
            <span style={{fontSize:'0.82rem',color:'#6b7280'}}>{job.area}</span>
          </div>

          {job.description && <p style={{fontSize:'0.88rem',color:'#374151',marginBottom:'1rem',lineHeight:1.5}}>{job.description}</p>}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem',background:'#f9fafb',borderRadius:'8px',padding:'0.75rem 1rem',marginBottom:'0.5rem'}}>
            <div style={{fontSize:'0.82rem'}}><strong style={{color:'#6b7280'}}>Property:</strong> {job.property_name}</div>
            <div style={{fontSize:'0.82rem'}}><strong style={{color:'#6b7280'}}>Date:</strong> {new Date(job.scheduled_date).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}</div>
          </div>
          {job.property_id && navigate && (
            <div style={{marginBottom:'1rem'}}>
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => { onClose(); navigate(`/propertydashboard/${job.property_id}`); }}>
                <FaBuilding /> View Property
              </button>
            </div>
          )}

          {/* Quote Section */}
          {canQuote && (
            <div style={{marginBottom:'1.25rem'}}>
              <h4 style={{fontSize:'0.95rem',marginBottom:'0.75rem'}}>Submit Quote</h4>

              {/* Quote qualification gate (P1-13) — shows what the FM requires
                  and which of your staff already cover each one. Hidden when
                  the job has no requirements. */}
              <RequiredQualificationsPanel
                jobId={job.id}
                onGateChange={setGateSatisfied}
                onAssignmentDataChange={setGateRequirements}
              />

              {/* Quote-acceptance staffing step (P1-16) — only renders when
                  the job has requirements; lets contractor pick which staff
                  to assign. */}
              {gateRequirements.length > 0 && (
                <AssignStaffStep
                  requirements={gateRequirements}
                  onChange={(ids) => setQuoteForm(f => ({ ...f, assigned_user_ids: ids }))}
                  onValidityChange={setAssignmentValid}
                />
              )}

              <form onSubmit={handleQuote}>
                <div className="cs-form-group">
                  <label>Message</label>
                  <textarea className="cs-textarea" placeholder="Thank you for choosing us..." value={quoteForm.message} onChange={e => setQuoteForm({...quoteForm, message: e.target.value})} />
                </div>
                <div className="cs-form-group">
                  <label>Notes</label>
                  <textarea className="cs-textarea" placeholder="Based on variables, this quote covers..." value={quoteForm.notes} onChange={e => setQuoteForm({...quoteForm, notes: e.target.value})} />
                </div>
                <div className="cs-form-group">
                  <label>Quote Document (PDF or image) *</label>
                  <input
                    className="cs-input"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    multiple
                    onChange={e => setQuoteForm({ ...quoteForm, files: Array.from(e.target.files || []) })}
                    required
                  />
                  <small style={{ color: '#6b7280', display: 'block', marginTop: '0.25rem' }}>Upload your quote as PDF or image</small>
                </div>
                <button
                  type="submit"
                  className="cs-btn cs-btn-success"
                  style={{ width: '100%' }}
                  disabled={gateRequirements.length > 0 && (!gateSatisfied || !assignmentValid)}
                  title={
                    gateRequirements.length > 0 && !gateSatisfied
                      ? 'Your team doesn\'t meet the required qualifications.'
                      : (gateRequirements.length > 0 && !assignmentValid
                          ? 'Assign enough qualified staff to cover every requirement.'
                          : '')
                  }
                >
                  <FaPaperPlane /> Submit Quote
                </button>
              </form>
            </div>
          )}

          {/* Existing Quotes */}
          {job.quotes && job.quotes.length > 0 && (
            <div style={{marginBottom:'1.25rem'}}>
              <h4 style={{fontSize:'0.95rem',marginBottom:'0.5rem'}}>Your Quote</h4>
              {job.quotes.map(q => (
                <div key={q.id} style={{background:'#f9fafb',borderRadius:'8px',padding:'0.875rem',border:'1px solid #e5e7eb',marginBottom:'0.5rem'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.25rem'}}>
                    <span style={{fontSize:'0.95rem',fontWeight:600,color:'#059669'}}>
                      {q.price > 0 ? `£${parseFloat(q.price).toFixed(2)}` : 'Quote document submitted'}
                    </span>
                    <StatusBadge status={q.status} size="small" />
                  </div>
                  {q.message && <p style={{fontSize:'0.82rem',color:'#374151',margin:'0.25rem 0'}}>{q.message}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Staff Assignment */}
          {canAssignStaff && (
            <div style={{marginBottom:'1.25rem'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.5rem'}}>
                <h4 style={{fontSize:'0.95rem',margin:0}}>Assigned Staff</h4>
                <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setShowAssign(!showAssign)}>
                  <FaPlus /> Add Staff
                </button>
              </div>

              {job.staff && job.staff.map(s => (
                <div key={s.id} style={{display:'flex',alignItems:'center',gap:'0.6rem',padding:'0.5rem 0',borderBottom:'1px solid #f3f4f6'}}>
                  <FaUserCircle style={{color:'#d1d5db',fontSize:'1.2rem'}} />
                  <div>
                    <span style={{fontWeight:600,fontSize:'0.85rem'}}>{s.first_name} {s.last_name}</span>
                    <span style={{fontSize:'0.75rem',color:'#6b7280',display:'block'}}>
                      {s.signed_in_at ? `In: ${new Date(s.signed_in_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}` : 'Not signed in'}
                    </span>
                  </div>
                </div>
              ))}

              {showAssign && (
                <div style={{marginTop:'0.75rem',padding:'0.75rem',background:'#f9fafb',borderRadius:'8px'}}>
                  <div style={{display:'flex',gap:'0.5rem',marginBottom:'0.5rem',flexWrap:'wrap'}}>
                    <input className="cs-input" placeholder="Search staff by name or email..." value={staffSearch} onChange={e => setStaffSearch(e.target.value)} style={{flex:1,minWidth:'140px'}} />
                    {allTagNames.length > 0 && (
                      <select className="cs-select" value={staffTagFilter} onChange={e => setStaffTagFilter(e.target.value)} style={{minWidth:'120px'}} title="Filter by tag">
                        <option value="">All tags</option>
                        {allTagNames.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  {staffResults.filter(s => !job.staff?.some(js => js.user_id === s.id)).map(s => (
                    <div key={s.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0.4rem 0'}}>
                      <span style={{fontSize:'0.85rem'}}>{s.first_name} {s.last_name}</span>
                      <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => onAssignStaff(s.id)}>Assign</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Attachments */}
          {job.attachments && job.attachments.length > 0 && (
            <div style={{marginBottom:'1.25rem'}}>
              <h4 style={{fontSize:'0.95rem',marginBottom:'0.5rem'}}>Attachments ({job.attachments.length})</h4>
              <div style={{display:'flex',flexWrap:'wrap',gap:'0.5rem'}}>
                {job.attachments.map(a => {
                  const docUrl = (a.download_url ? getApiEndpoint(a.download_url) : undefined);
                  return (
                    <a key={a.id} href={docUrl} target="_blank" rel="noopener noreferrer" style={{display:'flex',alignItems:'center',gap:'0.4rem',padding:'0.5rem 0.75rem',background:'#f3f4f6',borderRadius:'8px',fontSize:'0.85rem',textDecoration:'none',color:'#374151',cursor:'pointer'}}>
                      <FaPaperclip style={{color:'#6b7280'}} />
                      <span>{a.original_filename}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Job Notes */}
          {['approved', 'active', 'completed'].includes(job.status) && (
            <div>
              <h4 style={{fontSize:'0.95rem',marginBottom:'0.5rem'}}>Notes</h4>
              <div style={{display:'flex',gap:'0.5rem',marginBottom:'0.75rem'}}>
                <input className="cs-input" placeholder="Add a note..." value={noteText} onChange={e => setNoteText(e.target.value)} />
                <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={addNote}>Add</button>
              </div>
              {job.notes && job.notes.length > 0 ? (
                job.notes.map(n => (
                  <div key={n.id} style={{padding:'0.4rem 0',borderBottom:'1px solid #f3f4f6'}}>
                    <span style={{fontSize:'0.78rem',fontWeight:600}}>{n.first_name} {n.last_name}</span>
                    <p style={{fontSize:'0.82rem',color:'#6b7280',margin:'0.15rem 0'}}>{n.note_text}</p>
                    <span style={{fontSize:'0.7rem',color:'#9ca3af'}}>{new Date(n.created_at).toLocaleString('en-GB')}</span>
                  </div>
                ))
              ) : (
                <p style={{fontSize:'0.82rem',color:'#9ca3af',margin:0}}>No notes yet. Add one above.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractorDashboard;
